import React, { createContext, useContext, useReducer, useCallback, useEffect, useMemo } from 'react';
import type {
  Note,
  NoteInput,
  NoteStats,
  SessionStats,
  Settings,
  FlashcardState,
  PitchClass,
  Accidental
} from '../types/music';
import { useLocalStorage, STORAGE_KEYS } from '../hooks/useLocalStorage';
import { buildActiveDeck, DEFAULT_ADAPTIVE_CONFIG, getProgressionStageRange, getCurrentProgressionStage } from '../utils/adaptiveLogic';
import { getNextNote, updateRecentNotes } from '../utils/noteSelection';
import { inputMatchesNote } from '../utils/noteMappings';

// Default settings
const DEFAULT_SETTINGS: Settings = {
  clefSelection: 'bass',
  keySignature: 'C',
  includeSharps: false,
  includeFlats: false,
  naturalsOnly: true,
  trebleRange: { low: 'C4', high: 'G5', label: 'Easy (C4 - G5)' },
  bassRange: { low: 'C3', high: 'C4', label: 'Easy (C3 - C4)' },
  showPiano: true,
  inputMode: 'mixed',
  playAudio: false,
  colorCodeNotes: false,
  instrumentId: 'keyboard_grand'
};

// Default session stats
const DEFAULT_SESSION_STATS: SessionStats = {
  totalCards: 0,
  correctAnswers: 0,
  incorrectAnswers: 0,
  totalResponseTime: 0,
  averageResponseTime: 0,
  recentResults: [],
  currentLevel: 'beginner'
};

// State shape
interface PracticeState {
  settings: Settings;
  sessionStats: SessionStats;
  noteStats: Map<string, NoteStats>;
  currentCard: FlashcardState | null;
  activeDeck: Note[];
  recentNotes: string[];
  isStarted: boolean;
  pianoHighlight: {
    pitchClass: PitchClass | null;
    accidental: Accidental | null;
    type: 'correct' | 'incorrect' | 'none';
  };
  feedbackMessage: string | null;
  progressionStages: {
    treble: number;
    bass: number;
  };
}

// Action types
type PracticeAction =
  | { type: 'START_SESSION' }
  | { type: 'NEXT_CARD'; note: Note }
  | { type: 'REVEAL_CARD'; wasCorrect: boolean; responseTime: number }
  | { type: 'SET_PIANO_HIGHLIGHT'; pitchClass: PitchClass | null; accidental: Accidental | null; highlightType: 'correct' | 'incorrect' | 'none' }
  | { type: 'SET_FEEDBACK'; message: string | null }
  | { type: 'UPDATE_SETTINGS'; settings: Partial<Settings> }
  | { type: 'UPDATE_DECK'; deck: Note[] }
  | { type: 'UPDATE_RECENT_NOTES'; noteId: string }
  | { type: 'RECORD_ATTEMPT'; noteId: string; correct: boolean; responseTime: number }
  | { type: 'UPDATE_PROGRESSION'; clef: 'treble' | 'bass'; stage: number }
  | { type: 'RESET_SESSION' }
  | { type: 'LOAD_SAVED_STATE'; noteStats: Map<string, NoteStats>; sessionStats: SessionStats; progressionStages: { treble: number; bass: number } };

// Reducer
function practiceReducer(state: PracticeState, action: PracticeAction): PracticeState {
  switch (action.type) {
    case 'START_SESSION':
      return {
        ...state,
        isStarted: true,
        feedbackMessage: null
      };

    case 'NEXT_CARD':
      return {
        ...state,
        currentCard: {
          note: action.note,
          isRevealed: false,
          startTime: Date.now(),
          endTime: null,
          wasCorrect: null
        },
        pianoHighlight: { pitchClass: null, accidental: null, type: 'none' },
        feedbackMessage: null
      };

    case 'REVEAL_CARD':
      if (!state.currentCard) return state;
      return {
        ...state,
        currentCard: {
          ...state.currentCard,
          isRevealed: true,
          endTime: Date.now(),
          wasCorrect: action.wasCorrect
        },
        sessionStats: {
          ...state.sessionStats,
          totalCards: state.sessionStats.totalCards + 1,
          correctAnswers: state.sessionStats.correctAnswers + (action.wasCorrect ? 1 : 0),
          incorrectAnswers: state.sessionStats.incorrectAnswers + (action.wasCorrect ? 0 : 1),
          totalResponseTime: state.sessionStats.totalResponseTime + action.responseTime,
          averageResponseTime: (state.sessionStats.totalResponseTime + action.responseTime) / (state.sessionStats.totalCards + 1),
          recentResults: [
            ...state.sessionStats.recentResults.slice(-(DEFAULT_ADAPTIVE_CONFIG.rollingWindowSize - 1)),
            {
              noteId: state.currentCard.note.id,
              correct: action.wasCorrect,
              responseTime: action.responseTime,
              timestamp: Date.now()
            }
          ]
        }
      };

    case 'SET_PIANO_HIGHLIGHT':
      return {
        ...state,
        pianoHighlight: {
          pitchClass: action.pitchClass,
          accidental: action.accidental,
          type: action.highlightType
        }
      };

    case 'SET_FEEDBACK':
      return {
        ...state,
        feedbackMessage: action.message
      };

    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: { ...state.settings, ...action.settings }
      };

    case 'UPDATE_DECK':
      return {
        ...state,
        activeDeck: action.deck
      };

    case 'UPDATE_RECENT_NOTES':
      return {
        ...state,
        recentNotes: updateRecentNotes(state.recentNotes, action.noteId)
      };

    case 'RECORD_ATTEMPT': {
      const existingStats = state.noteStats.get(action.noteId);
      const newStats: NoteStats = existingStats ? {
        ...existingStats,
        attempts: existingStats.attempts + 1,
        correct: existingStats.correct + (action.correct ? 1 : 0),
        incorrect: existingStats.incorrect + (action.correct ? 0 : 1),
        totalResponseTime: existingStats.totalResponseTime + (action.correct ? action.responseTime : 0),
        averageResponseTime: action.correct
          ? (existingStats.totalResponseTime + action.responseTime) / (existingStats.correct + 1)
          : existingStats.averageResponseTime,
        recentResponseTimes: action.correct
          ? [...existingStats.recentResponseTimes.slice(-9), action.responseTime]
          : existingStats.recentResponseTimes
      } : {
        noteId: action.noteId,
        attempts: 1,
        correct: action.correct ? 1 : 0,
        incorrect: action.correct ? 0 : 1,
        totalResponseTime: action.correct ? action.responseTime : 0,
        averageResponseTime: action.correct ? action.responseTime : 0,
        recentResponseTimes: action.correct ? [action.responseTime] : []
      };

      const newNoteStats = new Map(state.noteStats);
      newNoteStats.set(action.noteId, newStats);

      return {
        ...state,
        noteStats: newNoteStats
      };
    }

    case 'UPDATE_PROGRESSION':
      return {
        ...state,
        progressionStages: {
          ...state.progressionStages,
          [action.clef]: action.stage
        }
      };

    case 'RESET_SESSION':
      return {
        ...state,
        sessionStats: DEFAULT_SESSION_STATS,
        noteStats: new Map(),
        currentCard: null,
        recentNotes: [],
        isStarted: false,
        pianoHighlight: { pitchClass: null, accidental: null, type: 'none' },
        feedbackMessage: null,
        progressionStages: { treble: 1, bass: 1 }
      };

    case 'LOAD_SAVED_STATE':
      return {
        ...state,
        noteStats: action.noteStats,
        sessionStats: action.sessionStats,
        progressionStages: action.progressionStages
      };

    default:
      return state;
  }
}

// Initial state
const initialState: PracticeState = {
  settings: DEFAULT_SETTINGS,
  sessionStats: DEFAULT_SESSION_STATS,
  noteStats: new Map(),
  currentCard: null,
  activeDeck: [],
  recentNotes: [],
  isStarted: false,
  pianoHighlight: { pitchClass: null, accidental: null, type: 'none' },
  feedbackMessage: null,
  progressionStages: { treble: 1, bass: 1 }
};

// Context type
interface PracticeContextType {
  state: PracticeState;
  startSession: () => void;
  nextCard: () => void;
  revealCard: (wasCorrect: boolean) => void;
  handleNoteInput: (input: NoteInput) => void;
  handleClickReveal: () => void;
  updateSettings: (settings: Partial<Settings>) => void;
  resetSession: () => void;
  clearPianoHighlight: () => void;
}

const PracticeContext = createContext<PracticeContextType | null>(null);

// Provider component
export function PracticeProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(practiceReducer, initialState);

  // Persist settings to localStorage
  const [savedSettings, setSavedSettings] = useLocalStorage<Settings>(
    STORAGE_KEYS.SETTINGS,
    DEFAULT_SETTINGS
  );

  // Persist note stats (convert Map to/from object for JSON)
  const [savedNoteStats, setSavedNoteStats] = useLocalStorage<Record<string, NoteStats>>(
    STORAGE_KEYS.NOTE_STATS,
    {}
  );

  // Persist session stats
  const [savedSessionStats, setSavedSessionStats] = useLocalStorage<SessionStats>(
    STORAGE_KEYS.SESSION_STATS,
    DEFAULT_SESSION_STATS
  );

  // Persist progression stages
  const [savedProgression, setSavedProgression] = useLocalStorage<{ treble: number; bass: number }>(
    STORAGE_KEYS.PROGRESSION_STAGE,
    { treble: 1, bass: 1 }
  );

  // Load saved state on mount
  useEffect(() => {
    dispatch({
      type: 'UPDATE_SETTINGS',
      settings: savedSettings
    });
    dispatch({
      type: 'LOAD_SAVED_STATE',
      noteStats: new Map(Object.entries(savedNoteStats)),
      sessionStats: savedSessionStats,
      progressionStages: savedProgression
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save settings when they change
  useEffect(() => {
    setSavedSettings(state.settings);
  }, [state.settings, setSavedSettings]);

  // Save note stats when they change
  useEffect(() => {
    const statsObject = Object.fromEntries(state.noteStats);
    setSavedNoteStats(statsObject);
  }, [state.noteStats, setSavedNoteStats]);

  // Save session stats when they change
  useEffect(() => {
    setSavedSessionStats(state.sessionStats);
  }, [state.sessionStats, setSavedSessionStats]);

  // Save progression when it changes
  useEffect(() => {
    setSavedProgression(state.progressionStages);
  }, [state.progressionStages, setSavedProgression]);

  // Build the active deck whenever settings change
  useEffect(() => {
    const { settings, progressionStages } = state;

    let deck: Note[] = [];

    // Determine which clef(s) to include
    const clefs: ('treble' | 'bass')[] = settings.clefSelection === 'mixed'
      ? ['treble', 'bass']
      : [settings.clefSelection];

    for (const clef of clefs) {
      // Get the current progression stage range
      const stage = progressionStages[clef];
      const { low, high } = getProgressionStageRange(clef, stage);

      // Build deck for this clef
      const clefDeck = buildActiveDeck(
        clef,
        low,
        high,
        !settings.naturalsOnly || settings.includeSharps === false,
        settings.includeSharps && !settings.naturalsOnly,
        settings.includeFlats && !settings.naturalsOnly
      );

      deck = [...deck, ...clefDeck];
    }

    dispatch({ type: 'UPDATE_DECK', deck });
  }, [state.settings, state.progressionStages]);

  // Start the practice session
  const startSession = useCallback(() => {
    dispatch({ type: 'START_SESSION' });
    // Get first card
    const note = getNextNote(state.activeDeck, state.noteStats, state.recentNotes);
    if (note) {
      dispatch({ type: 'NEXT_CARD', note });
      dispatch({ type: 'UPDATE_RECENT_NOTES', noteId: note.id });
    }
  }, [state.activeDeck, state.noteStats, state.recentNotes]);

  // Move to the next card
  const nextCard = useCallback(() => {
    // Check if we should update difficulty
    const { sessionStats, progressionStages, settings } = state;
    const currentClef = state.currentCard?.note.clef || settings.clefSelection;

    if (currentClef !== 'mixed' && (currentClef === 'treble' || currentClef === 'bass')) {
      const newStage = getCurrentProgressionStage(
        currentClef,
        sessionStats.recentResults,
        progressionStages[currentClef]
      );

      if (newStage !== progressionStages[currentClef]) {
        dispatch({ type: 'UPDATE_PROGRESSION', clef: currentClef, stage: newStage });
      }
    }

    // Get next note
    const note = getNextNote(state.activeDeck, state.noteStats, state.recentNotes);
    if (note) {
      dispatch({ type: 'NEXT_CARD', note });
      dispatch({ type: 'UPDATE_RECENT_NOTES', noteId: note.id });
    }
  }, [state]);

  // Reveal the current card
  const revealCard = useCallback((wasCorrect: boolean) => {
    if (!state.currentCard || state.currentCard.isRevealed) return;

    const responseTime = Date.now() - state.currentCard.startTime;

    dispatch({ type: 'REVEAL_CARD', wasCorrect, responseTime });
    dispatch({
      type: 'RECORD_ATTEMPT',
      noteId: state.currentCard.note.id,
      correct: wasCorrect,
      responseTime
    });

    // Show correct note on piano
    dispatch({
      type: 'SET_PIANO_HIGHLIGHT',
      pitchClass: state.currentCard.note.pitchClass,
      accidental: state.currentCard.note.accidental,
      highlightType: 'correct'
    });

    dispatch({
      type: 'SET_FEEDBACK',
      message: wasCorrect ? 'Correct!' : null
    });
  }, [state.currentCard]);

  // Handle note input from keyboard
  const handleNoteInput = useCallback((input: NoteInput) => {
    if (!state.currentCard || state.currentCard.isRevealed) return;
    if (state.settings.inputMode === 'click') return; // Keyboard disabled in click-only mode

    const isCorrect = inputMatchesNote(input, state.currentCard.note);

    if (isCorrect) {
      // Correct answer - reveal card
      revealCard(true);
    } else {
      // Incorrect answer - show red highlight on piano, do NOT reveal
      dispatch({
        type: 'SET_PIANO_HIGHLIGHT',
        pitchClass: input.pitchClass,
        accidental: input.accidental,
        highlightType: 'incorrect'
      });
      dispatch({ type: 'SET_FEEDBACK', message: 'Try again' });

      // Clear the red highlight after a short delay
      setTimeout(() => {
        dispatch({
          type: 'SET_PIANO_HIGHLIGHT',
          pitchClass: null,
          accidental: null,
          highlightType: 'none'
        });
        dispatch({ type: 'SET_FEEDBACK', message: null });
      }, 800);
    }
  }, [state.currentCard, state.settings.inputMode, revealCard]);

  // Handle click to reveal
  const handleClickReveal = useCallback(() => {
    if (!state.currentCard) return;

    if (state.currentCard.isRevealed) {
      // Already revealed - go to next card
      nextCard();
    } else {
      // Reveal the card (mark as not correct via keyboard)
      // In keyboard-only mode, clicking doesn't mark as correct
      const markCorrect = state.settings.inputMode === 'click';
      revealCard(markCorrect);
    }
  }, [state.currentCard, state.settings.inputMode, nextCard, revealCard]);

  // Update settings
  const updateSettings = useCallback((settings: Partial<Settings>) => {
    dispatch({ type: 'UPDATE_SETTINGS', settings });
  }, []);

  // Reset session
  const resetSession = useCallback(() => {
    dispatch({ type: 'RESET_SESSION' });
  }, []);

  // Clear piano highlight
  const clearPianoHighlight = useCallback(() => {
    dispatch({
      type: 'SET_PIANO_HIGHLIGHT',
      pitchClass: null,
      accidental: null,
      highlightType: 'none'
    });
  }, []);

  const contextValue = useMemo(() => ({
    state,
    startSession,
    nextCard,
    revealCard,
    handleNoteInput,
    handleClickReveal,
    updateSettings,
    resetSession,
    clearPianoHighlight
  }), [state, startSession, nextCard, revealCard, handleNoteInput, handleClickReveal, updateSettings, resetSession, clearPianoHighlight]);

  return (
    <PracticeContext.Provider value={contextValue}>
      {children}
    </PracticeContext.Provider>
  );
}

// Hook to use the practice context
export function usePractice(): PracticeContextType {
  const context = useContext(PracticeContext);
  if (!context) {
    throw new Error('usePractice must be used within a PracticeProvider');
  }
  return context;
}

// Helper hook to get just the state
export function usePracticeState(): PracticeState {
  const { state } = usePractice();
  return state;
}
